'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Edit2, Trash2, MapPin, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration: GET /api/assets?type=equipment
const equipment = [
  { id: 'eq-001', unit: 'EX-07', type: 'Excavator', make: 'Caterpillar', model: '320', year: 2019, serialNumber: 'CAT0320XEKF09821', status: 'on-site', gpsStatus: 'weak', assignedJob: 'FJ-2838', lastLocation: 'Block 14, Andrews TX', engineHours: 4820, nextService: '5,000 hrs', fuelLevel: 34, alerts: 1 },
  { id: 'eq-002', unit: 'CR-12', type: 'Crawler Crane', make: 'Manitowoc', model: '14000', year: 2020, serialNumber: 'MAN14000JC20044', status: 'on-site', gpsStatus: 'strong', assignedJob: 'FJ-2825', lastLocation: 'Pad 12, Loving County', engineHours: 2340, nextService: '2,500 hrs', fuelLevel: 71, alerts: 0 },
  { id: 'eq-003', unit: 'GN-03', type: 'Generator', make: 'Caterpillar', model: 'XQ350', year: 2021, serialNumber: 'CATXQ350PB21099', status: 'available', gpsStatus: 'strong', assignedJob: undefined, lastLocation: 'Yard, Midland TX', engineHours: 1120, nextService: '1,500 hrs', fuelLevel: 88, alerts: 0 },
  { id: 'eq-004', unit: 'TK-05', type: 'Water Tank Truck', make: 'Kenworth', model: 'T800', year: 2018, serialNumber: 'KWT800WX18004421', status: 'returning', gpsStatus: 'strong', assignedJob: 'FJ-2833', lastLocation: 'US-285 S', engineHours: 8720, nextService: 'OVERDUE', fuelLevel: 52, alerts: 1 },
  { id: 'eq-005', unit: 'CM-09', type: 'Cement Mixer', make: 'McNeilus', model: "S' Series", year: 2022, serialNumber: 'MCN2022SX09771', status: 'maintenance', gpsStatus: 'weak', assignedJob: undefined, lastLocation: 'Odessa Service Center', engineHours: 3440, nextService: 'In Service', fuelLevel: 20, alerts: 0 },
  { id: 'eq-006', unit: 'FL-02', type: 'Flare Stack', make: 'Combustion Tech', model: 'CT-400', year: 2020, serialNumber: 'CT400FX20002', status: 'available', gpsStatus: 'strong', assignedJob: undefined, lastLocation: 'Yard, Midland TX', engineHours: 680, nextService: '1,000 hrs', fuelLevel: 100, alerts: 0 },
];

export default function EquipmentTable() {
  const [search, setSearch] = useState('');
  const filtered = equipment.filter((e) => {
    const q = search.toLowerCase();
    return !q || e.unit.toLowerCase().includes(q) || e.type.toLowerCase().includes(q) || e.make.toLowerCase().includes(q);
  });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
        <input
          type="text"
          placeholder="Search equipment..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-all w-56"
        />
        <span className="text-xs text-zinc-600 ml-auto">{filtered.length} units</span>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Unit', 'Type / Make', 'Serial No.', 'Status', 'GPS', 'Assigned Job', 'Location', 'Engine Hrs', 'Fuel', 'Next Service', 'Actions'].map((h) => (
                <th key={`eq-h-${h}`} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {filtered.map((eq) => (
              <tr key={eq.id} className="group hover:bg-zinc-800/40 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs font-bold text-amber-400">{eq.unit}</span>
                    {eq.alerts > 0 && <AlertTriangle size={11} className="text-red-400" />}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs font-medium text-zinc-200">{eq.type}</p>
                  <p className="text-[10px] text-zinc-500">{eq.make} {eq.model}</p>
                </td>
                <td className="px-4 py-3 text-[10px] font-mono text-zinc-500 max-w-[110px] truncate">{eq.serialNumber}</td>
                <td className="px-4 py-3"><StatusBadge status={eq.status as any} /></td>
                <td className="px-4 py-3">
                  {eq.gpsStatus === 'lost' ? <span className="flex items-center gap-1 text-[11px] text-red-400"><WifiOff size={11} /> Lost</span>
                    : eq.gpsStatus === 'weak' ? <span className="flex items-center gap-1 text-[11px] text-amber-400"><Wifi size={11} /> Weak</span>
                    : <span className="flex items-center gap-1 text-[11px] text-emerald-400"><Wifi size={11} /> Strong</span>}
                </td>
                <td className="px-4 py-3">
                  {eq.assignedJob
                    ? <span className="font-mono text-xs text-amber-400">{eq.assignedJob}</span>
                    : <span className="text-xs text-zinc-600 italic">Unassigned</span>}
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-zinc-400 flex items-start gap-1 max-w-[130px]">
                    <MapPin size={9} className="mt-0.5 flex-shrink-0" />
                    <span className="truncate">{eq.lastLocation}</span>
                  </p>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-300 tabular-nums">{eq.engineHours.toLocaleString()} hrs</td>
                <td className="px-4 py-3">
                  <div className="h-1 w-14 bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${eq.fuelLevel}%`, backgroundColor: eq.fuelLevel < 25 ? '#F87171' : eq.fuelLevel < 40 ? '#F59E0B' : '#34D399' }} />
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{eq.fuelLevel}%</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-mono font-medium ${eq.nextService === 'OVERDUE' ? 'text-red-400' : eq.nextService === 'In Service' ? 'text-amber-400' : 'text-zinc-400'}`}>
                    {eq.nextService}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => toast.info(`Editing ${eq.unit}`)} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all"><Edit2 size={13} /></button>
                    <button onClick={() => toast.error(`${eq.unit} removed`)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}